import { Circle, CircleOutlined, Delete, DoubleArrow, Undo } from "@mui/icons-material";
import { Box, Button, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  CANVAS_SIZE,
  createMaskFromCanvas,
  getMaskCoordinates,
  isMaskEmpty,
  RLEMask,
} from "../util/mask";

type DrawMode = "draw" | "erase";

function MaskDrawingTool(props: {
  maskImg: HTMLImageElement;
  onMaskChange: (mask: RLEMask, img: HTMLImageElement) => void;
  actionText?: string;
}) {
  const undoStack = useRef<string[]>([]); // Undo stack for canvas states
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [drawMode, setDrawMode] = useState<DrawMode>("draw");
  const [penSize, setPenSize] = useState(25);
  const [canvasState, setCanvasState] = useState<string | null>(null);
  const [isEmpty, setIsEmpty] = useState(true);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx) {
      setIsEmpty(isMaskEmpty(ctx));
    }
  }, [canvasState]); // Trigger on canvas state change

  const startDrawing = useCallback(
    (
      e:
        | React.MouseEvent<HTMLCanvasElement>
        | React.TouchEvent<HTMLCanvasElement>
        | MouseEvent
        | TouchEvent,
    ) => {
      e.cancelable && e.preventDefault();
      if (!canvasRef.current) return;
      const ctx = canvasRef.current.getContext("2d");
      if (!ctx) return;

      undoStack.current.push(canvasRef.current.toDataURL());

      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = drawMode === "draw" ? "black" : "white";
      ctx.globalCompositeOperation = "source-over";

      ctx.beginPath();

      const { x, y } = getMaskCoordinates(e, canvasRef.current, CANVAS_SIZE);
      // Limit the canvas to CANVAS_SIZE
      ctx.moveTo(
        Math.min(Math.max(x, 0), CANVAS_SIZE),
        Math.min(Math.max(y, 0), CANVAS_SIZE),
      );

      const draw = (evt: MouseEvent | TouchEvent) => {
        evt.cancelable && evt.preventDefault();
        if (!ctx || !canvasRef.current) return;
        const { x, y } = getMaskCoordinates(evt, canvasRef.current, CANVAS_SIZE);
        ctx.lineWidth = penSize;
        ctx.lineTo(
          Math.min(Math.max(x, 0), CANVAS_SIZE),
          Math.min(Math.max(y, 0), CANVAS_SIZE),
        );
        ctx.stroke();
      };

      const stopDrawing = () => {
        document.removeEventListener("mousemove", draw);
        document.removeEventListener("mouseup", stopDrawing);
        document.removeEventListener("touchmove", draw);
        document.removeEventListener("touchend", stopDrawing);
        setCanvasState(canvasRef.current?.toDataURL() || null);
      };

      document.addEventListener("mousemove", draw);
      document.addEventListener("mouseup", stopDrawing);
      document.addEventListener("touchmove", draw);
      document.addEventListener("touchend", stopDrawing);
    },
    [drawMode, penSize],
  );

  const handlePenSizeChange = (
    _: React.MouseEvent<HTMLElement>,
    newValue: number | number[],
  ) => {
    if (typeof newValue === "number") setPenSize(newValue);
  };

  const exportMask = () => {
    if (!canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    // If the canvas is empty, return null
    if (isMaskEmpty(ctx)) {
      props.onMaskChange(null, null);
      return;
    }

    const { rleMask, img } = createMaskFromCanvas(ctx);
    props.onMaskChange(rleMask, img);
  };

  const clearCanvas = () => {
    const ctx = canvasRef.current?.getContext("2d");
    if (ctx && canvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
  };

  const handleUndo = () => {
    if (undoStack.current.length === 0 || !canvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    const lastState = undoStack.current.pop();
    const img = new Image();

    img.onload = () => {
      // Clear the current canvas before drawing the previous state
      ctx?.clearRect(0, 0, canvasRef.current!.width, canvasRef.current!.height);
      ctx?.drawImage(img, 0, 0);
    };

    img.src = lastState!;
  };

  useEffect(() => {
    // Load persisted canvas state when component mounts or dialog reopens
    if ((canvasState || props.maskImg) && canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      const img = new Image();
      img.src = canvasState || props.maskImg.src;
      img.onload = () => ctx?.drawImage(img, 0, 0);
    }
  }, [canvasState, props.maskImg]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center">
      <Stack direction="row" mb={2} gap={2} width="100%">
        <ToggleButtonGroup
          value={drawMode}
          exclusive
          onChange={(_, mode) => mode && setDrawMode(mode)}
          aria-label="draw mode"
          size="small"
        >
          <ToggleButton value="draw" aria-label="draw">
            <Circle fontSize="small" sx={{ mt: -0.5, mr: 0.5 }} />
            Draw
          </ToggleButton>
          <ToggleButton value="erase" aria-label="erase">
            <CircleOutlined
              fontSize="small"
              sx={{
                mt: -0.5,
                mr: 0.5,
              }}
            />
            Erase
          </ToggleButton>
        </ToggleButtonGroup>
        <Box flexGrow={1} />
        <ToggleButtonGroup
          value={penSize}
          exclusive
          onChange={handlePenSizeChange}
          aria-label="brush size"
        >
          {[5, 25, 50, 100].map((size) => (
            <ToggleButton key={size} value={size} aria-label={`brush size ${size}`}>
              <div
                style={{
                  width: size / 5 + 4,
                  height: size / 5 + 4,
                  borderRadius: "50%",
                  backgroundColor: "black",
                }}
              />
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Stack>
      <canvas
        ref={canvasRef}
        width={CANVAS_SIZE}
        height={CANVAS_SIZE}
        style={{
          border: "1px solid black",
          background: "white",
          cursor: "crosshair",
          width: "100%",
          maxWidth: "400px",
        }}
        onMouseDown={startDrawing}
        onTouchStart={startDrawing}
        onMouseUp={() => setCanvasState(canvasRef.current?.toDataURL() || null)} // Save canvas state on draw end
        onMouseLeave={() => setCanvasState(canvasRef.current?.toDataURL() || null)} // Save canvas state on draw end
      />

      <Stack direction="row" mt={2} gap={2} width="100%">
        <Button
          variant="outlined"
          color="error"
          onClick={clearCanvas}
          startIcon={<Delete />}
          disabled={isEmpty}
        >
          Clear
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleUndo}
          startIcon={<Undo />}
          disabled={isEmpty}
        >
          Undo
        </Button>
        <Box flexGrow={1} />
        <Button
          variant="contained"
          onClick={exportMask}
          startIcon={<DoubleArrow />}
          disabled={isEmpty}
        >
          {props.actionText || "Save"}
        </Button>
      </Stack>
    </Box>
  );
}

export default MaskDrawingTool;
